import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router'
import Logo from '../../Images/Branding/cc-logo-white.svg'
import LogoBlue from '../../Images/Branding/cc-logo-blue.svg'
import { FetchEventsResponse, PaginationResponse } from '../../swagger'
import { displayFormattedDashDate } from '../../utils/displayFormattedDate'
import { createSpacetime } from './Events'
import { EventSortBy } from './EventEnums'
import { timezoneTitleCase } from '../../utils/timeFormatUtilities'
import { IconButton, Tooltip, useTheme } from '@mui/material'
import LoadingProgress from '../Elements/LoadingProgress'
import { GridValueFormatterParams } from '@mui/x-data-grid'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { GridCallbackDetails } from '@mui/x-data-grid'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'

const EventSummaryTable: React.FC<{
  events: FetchEventsResponse[]
  isLoading: boolean
  handlePaginationChange: (pagination: PaginationResponse) => void
  pagination: PaginationResponse
  checkboxSelection?: boolean
  onSelectionModelChange?: (
    newSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void
  selectionModel?: GridRowSelectionModel
  onEventClick?: (event: FetchEventsResponse) => void
}> = ({
  isLoading,
  events,
  handlePaginationChange,
  pagination,
  checkboxSelection,
  onSelectionModelChange,
  selectionModel,
  onEventClick,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isLeadershipRoute = location.pathname.startsWith(
    '/events/leadership-training'
  )

  const handleRowNavigation = React.useCallback(
    (eventRow: FetchEventsResponse) => {
      if (onEventClick) {
        // passed by internal events, so we navigate to internal events route.
        onEventClick(eventRow)
        return
      }

      navigate(
        {
          pathname: `/events/event-details/${eventRow.eventKey}`,
        },
        {
          state: { eventName: eventRow.name },
        }
      )
    },
    [navigate, onEventClick]
  )

  const handleSorting = (
    pageInformation: PaginationResponse
  ): PaginationResponse => {
    /**
     * There is a default orderBy on the table request so this
     * should never be undefined the fromDate which is set on
     * the events file where the filters are setup
     */
    const orderBy = pageInformation.orderBy?.[0]

    for (const key in orderBy) {
      let orderByProperty = key
      switch (key) {
        case 'date':
          orderByProperty = EventSortBy.StartDate
          break
        case 'time':
          orderByProperty = EventSortBy.StartTime
          break
        case 'createdBy':
          orderByProperty = EventSortBy.CreatedBy
          break
        default:
          break
      }
      return {
        ...pageInformation,
        orderBy: [{ [orderByProperty]: orderBy[key] }],
      }
    }

    return pageInformation
  }

  if (isLoading) {
    return <LoadingProgress />
  }

  const registeredColumn: ActionableTableColumn = {
    fieldName: 'isRegistered',
    columnHeaderName: t('Events.Table.Header.Registered', 'Registered'),
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    width: 140,
    renderCell: (params) =>
      params.row.isRegistered ? (
        <Tooltip
          title={t('EventRegistrantCard.Tooltip.AddAttending', 'Attending')}
        >
          <IconButton aria-label="attending" color="success">
            <EventAvailableIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip
          title={t('EventRegistrantCard.Tooltip.NotAttending', 'Not attending')}
        >
          <IconButton aria-label="Not attending">
            <EventBusyIcon />
          </IconButton>
        </Tooltip>
      ),
  }

  const ActionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      // No field name since we show an image
      fieldName: '',
      columnHeaderName: t('Events.Table.Header.Image', ''),
      maxWidth: 72,
      align: 'left',
      renderCell: (params) => {
        const image = (
          <img
            src={params.row.isOnline ? LogoBlue : Logo}
            alt={t('Events.Table.EventImage.AltText', 'Event Image')}
            style={{
              height: 40,
              width: 40,
              padding: theme.spacing(1),
              ...(params.row.isOnline
                ? {
                    background: theme.palette.customBackground.logo,
                  }
                : {
                    background: theme.palette.primary.main,
                  }),
            }}
          />
        )

        return (
          <div id="eventImage" style={{ padding: theme.spacing(1) }}>
            {image}
          </div>
        )
      },
      sortable: false,
    },
    {
      fieldName: 'name',
      columnHeaderName: t('Events.Table.Header.EventName', 'Event Name'),
      align: 'left',
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return params.value
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'category',
      columnHeaderName: t(
        'Events.Table.Header.EventCategory',
        'Event Category'
      ),
      align: 'left',
    },
    {
      fieldName: 'eventType',
      columnHeaderName: t('Events.Table.Header.EventType', 'Event Type'),
      align: 'left',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'city',
      columnHeaderName: t('Events.Table.Header.City', 'City'),
      align: 'left',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'date',
      columnHeaderName: t('Events.Table.Header.Date', 'Date'),
      align: 'left',
      valueGetter: (params) => {
        return `${displayFormattedDashDate(
          params.row.startDate.toISOString(),
          params.row.endDate?.toISOString() ?? ''
        )}`
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'time',
      columnHeaderName: t('Events.Table.Header.Time', 'Time'),
      align: 'left',
      valueGetter: (params) => {
        const time = `${createSpacetime(params.row).time()} - ${createSpacetime(
          params.row,
          false
        ).time()} ${timezoneTitleCase(params.row.timezone)}`

        return time
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'attendees',
      columnHeaderName: t(
        'Events.Registrants.Table.Header.Attendees',
        'Attendees'
      ),
      align: 'left',
      sortable: false,
    },
    {
      fieldName: 'createdBy',
      columnHeaderName: t(
        'Events.Registrants.Table.Header.CreatedBy',
        'Created By'
      ),
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return params.value
      },
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    // Conditionally include the Registered column only for leadership training.
    ...(isLeadershipRoute ? [registeredColumn] : []),
  ]

  const handleSelectionModelChange = (
    newRowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (onSelectionModelChange) {
      onSelectionModelChange(newRowSelectionModel, details)
    }
  }

  // Add eventKey as id to each event so ActionableTable uses it instead of array index
  const rows = events.map((event) => ({
    ...event,
    id: event.eventKey,
  }))

  return (
    <ActionableTable
      columns={ActionableTableColumnsHeader}
      rows={rows}
      noResultsMessage={t('Events.Table.NoEvents', 'No Events found')}
      customCssTable={{
        '&:hover': {
          cursor: 'pointer',
        },
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: theme.palette.textOrIcon.mapButton,
        },
      }}
      pagination={{
        ...pagination,
        page: pagination.page - 1,
      }}
      checkboxSelection={checkboxSelection}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleSelectionModelChange}
      handlePaginationChange={(pagination) => {
        pagination = handleSorting(pagination)
        return handlePaginationChange(pagination)
      }}
      defaultToGreenCheckboxes
      onRowClick={(eventClicked) => {
        handleRowNavigation(eventClicked.row as FetchEventsResponse)
      }}
    />
  )
}

export default EventSummaryTable
